import {
  Box,
  Divider,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { useDndContext, useDndMonitor, useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { IconMagnifyingGlass2 } from 'central-icons/IconMagnifyingGlass2';
import { IconPlusLarge } from 'central-icons/IconPlusLarge';
import classNames from 'classnames';
import mixpanel from 'mixpanel-browser';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { BuildADashTrackingEvents } from '@revelio/core';
import { Badge, BadgeColor, IconButton as UiIconButton } from '@revelio/ui';

import type { TabItem } from '../hooks/useTabOrder';
import {
  ANALYTICS_VIEWS,
  type AnalyticsEntity,
} from '../utils/revelio-primary-filter-entity-switch';
import {
  buildPathFromEntityType,
  entityTypeToFilterId,
  filterIdToEntityType,
} from '../utils/routing-helpers';
import { DraggableTabLink } from './DraggableTabLink';
import { usePageManager } from './PageManagerProvider/PageManagerProvider';
import styles from './PagesMenu.module.css';

const getAnalyticsPageId = (
  entity: AnalyticsEntity,
  label: string
): string | undefined =>
  ANALYTICS_VIEWS.find((v) => v.name === label)?.revelioPageIds[entity];

const POPOVER_WIDTH = '180px';
// Cap the scrollable tab list so the popover never runs off the viewport.
// Header + create/search controls stay sticky above. Uses min() to also
// shrink on short viewports.
const MENU_LIST_MAX_HEIGHT = 'min(480px, calc(100vh - 160px))';

const EmptyMessage = ({ children }: { children: ReactNode }) => (
  <Text className={styles.emptyMessage}>{children}</Text>
);

const ChevronDoubleRightIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M11.6665 13.3327L14.9998 9.99935L11.6665 6.66602"
      stroke="currentColor"
      strokeWidth="0.833333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.8335 13.3327L9.16683 9.99935L5.8335 6.66602"
      stroke="currentColor"
      strokeWidth="0.833333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type PagesMenuProps = {
  entity: AnalyticsEntity;
  menuTabs: TabItem[];
  activeMenuTabId: string | null;
};

export const PagesMenu = ({
  entity,
  menuTabs,
  activeMenuTabId,
}: PagesMenuProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    createPageDraft,
    deletePage,
    clonePage,
    clonePageDraft,
    updatePage,
    editPage,
  } = usePageManager();
  const navigate = useNavigate();
  const location = useLocation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  // Single source of truth: track whether a drag is active or just ended.
  // Real outside clicks close; drag drops (which fire a synthetic click on
  // the bar) are ignored. closeOnBlur is left off so Chakra's focus-based
  // close doesn't fight us.
  const { active: dragActive } = useDndContext();
  const ignoreOutsideUntilRef = useRef(0);

  useDndMonitor({
    onDragStart: () => {
      ignoreOutsideUntilRef.current = Number.POSITIVE_INFINITY;
    },
    onDragEnd: () => {
      ignoreOutsideUntilRef.current = Date.now() + 200;
    },
    onDragCancel: () => {
      ignoreOutsideUntilRef.current = Date.now() + 200;
    },
  });

  useOutsideClick({
    ref: popoverContentRef,
    handler: (e) => {
      if (!isOpen) return;
      if (dragActive) return;
      if (Date.now() < ignoreOutsideUntilRef.current) return;
      // Let the trigger's own click toggle handle close so we don't
      // close-then-reopen on the same click.
      if (e.target instanceof Node && triggerRef.current?.contains(e.target)) {
        return;
      }
      onClose();
    },
  });
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setSearch('');
    }
  }, [showSearch]);

  useEffect(() => {
    if (!isOpen) {
      setShowSearch(false);
      setSearch('');
    }
  }, [isOpen]);

  const filteredMenuTabs = search
    ? menuTabs.filter((t) =>
        t.label.toLowerCase().includes(search.toLowerCase())
      )
    : menuTabs;

  const highlight = (label: string): ReactNode => {
    if (!search) return label;
    const idx = label.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1) return label;
    return (
      <>
        {label.slice(0, idx)}
        <mark>{label.slice(idx, idx + search.length)}</mark>
        {label.slice(idx + search.length)}
      </>
    );
  };

  // Drop zone for bar -> menu drags (works even when popover is closed since
  // the trigger button is always rendered).
  const { setNodeRef: setTriggerDropRef, isOver: isTriggerOver } = useDroppable(
    {
      id: 'menu',
    }
  );

  // Auto-open the popover when a bar tab is dragged over the trigger so the
  // user can land the drop on a specific position inside the menu.
  useEffect(() => {
    if (isTriggerOver && !isOpen) onOpen();
  }, [isTriggerOver, isOpen, onOpen]);

  const handleCreate = () => {
    const entityFilterId = entityTypeToFilterId(entity);
    if (!entityFilterId) return;
    onClose();
    createPageDraft(filterIdToEntityType(entityFilterId));
    mixpanel.track(BuildADashTrackingEvents.CREATE_NEW_PAGE, {
      entityType: entityFilterId,
    });
  };

  const triggerActive = isOpen || isTriggerOver || activeMenuTabId !== null;

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-end"
      gutter={8}
      isLazy
      closeOnBlur={false}
    >
      <PopoverTrigger>
        <Box
          ref={(node) => {
            setTriggerDropRef(node);
            triggerRef.current = node;
          }}
          alignSelf="flex-start"
          marginLeft={-2}
          display="inline-flex"
          position="relative"
          borderRadius="full"
          {...(isTriggerOver && {
            outline: '2px dashed',
            outlineColor: 'green.300',
          })}
        >
          <IconButton
            aria-label="Open pages menu"
            data-testid="pages-menu-trigger"
            icon={<ChevronDoubleRightIcon />}
            variant="flat"
            colorScheme="gray"
            rounded="full"
            size="sm"
            className={classNames(
              styles.trigger,
              triggerActive && styles.triggerActive
            )}
          />
          {activeMenuTabId !== null && (
            <Box
              position="absolute"
              top="-2px"
              right="0"
              zIndex={1}
              pointerEvents="none"
            >
              <Badge
                data-testid="pages-menu-active-badge"
                color={BadgeColor.LIGHT_BLUE}
                size="sm"
              />
            </Box>
          )}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          ref={popoverContentRef}
          className={styles.popover}
          width={POPOVER_WIDTH}
          minWidth={POPOVER_WIDTH}
          maxWidth={POPOVER_WIDTH}
          borderRadius="6px"
          data-testid="pages-menu-popover"
        >
          <PopoverBody p={0}>
            <Flex
              align="center"
              justify="space-between"
              px="14px"
              pt="8px"
              pb="6px"
            >
              <Text className={styles.headerText}>Pages</Text>
              <Flex align="center" gap={1}>
                {menuTabs.length >= 5 && (
                  <IconButton
                    aria-label="Toggle search"
                    data-testid="pages-menu-search-toggle"
                    icon={<IconMagnifyingGlass2 size={14} />}
                    size="xs"
                    borderRadius="4px"
                    colorScheme={showSearch ? 'gray' : 'navyBlue'}
                    variant={showSearch ? 'solid' : 'ghost'}
                    onClick={() => setShowSearch((prev) => !prev)}
                  />
                )}
                <UiIconButton
                  aria-label="Create new page"
                  data-testid="pages-menu-create"
                  icon={<IconPlusLarge size={14} />}
                  size="sm"
                  shape="squared"
                  variant="ghost"
                  className={styles.navyIcon}
                  onClick={handleCreate}
                />
              </Flex>
            </Flex>
            {showSearch && (
              <Box px="10px" pb="6px">
                <InputGroup size="sm">
                  <Input
                    ref={searchInputRef}
                    data-testid="pages-menu-search-input"
                    placeholder="Search pages..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <InputRightElement>
                      <UiIconButton
                        aria-label="Clear search"
                        size="sm"
                        variant="ghost"
                        shape="rounded"
                        className={styles.navyIcon}
                        icon={<IconCrossLarge size={12} />}
                        onClick={() => {
                          setSearch('');
                          searchInputRef.current?.focus();
                        }}
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
            )}
            <Divider className={styles.divider} />
            <SortableContext
              items={filteredMenuTabs.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <Flex
                className={styles.scrollArea}
                direction="column"
                py="6px"
                minHeight="32px"
                maxHeight={MENU_LIST_MAX_HEIGHT}
                overflowY="auto"
              >
                {(() => {
                  if (menuTabs.length === 0) {
                    return (
                      <EmptyMessage>
                        Drag tabs here or add a new page
                      </EmptyMessage>
                    );
                  }
                  if (filteredMenuTabs.length === 0) {
                    return <EmptyMessage>No pages found</EmptyMessage>;
                  }
                  return filteredMenuTabs.map((tab) => {
                    const page = tab.page;
                    // Analytics tabs don't have a `page` object (templates
                    // live in localTemplates, not the user's pages), but the
                    // template id matches the RevelioPageId so editPage /
                    // clonePage resolve via findPage().
                    const actionPageId =
                      page?.id ?? getAnalyticsPageId(entity, tab.label);
                    const trailing = actionPageId ? (
                      <Menu placement="bottom-end" offset={[0, 0]}>
                        <MenuButton
                          as={IconButton}
                          icon={<IconDotGrid1x3Vertical size={14} />}
                          aria-label="Page actions"
                          data-testid={`pages-menu-${tab.kind}-${tab.id}-actions`}
                          size="xs"
                          variant="ghost"
                          colorScheme="navyBlue"
                          borderRadius="4px"
                          marginRight="2"
                          tabIndex={0}
                          visibility="hidden"
                          _groupHover={{ visibility: 'visible' }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <MenuList
                          className={styles.subMenu}
                          minW={24}
                          data-testid={`pages-menu-${tab.kind}-${tab.id}-menu-list`}
                        >
                          <MenuItem
                            fontSize="sm"
                            onClick={() => {
                              onClose();
                              if (page) {
                                editPage(actionPageId);
                              } else {
                                // Revelio templates are immutable; "edit"
                                // opens the template as an editable draft
                                // via the clone route (editMode=true).
                                navigate(
                                  buildPathFromEntityType(entity, {
                                    templateId: actionPageId,
                                    action: 'clone',
                                    search: location.search,
                                  })
                                );
                              }
                              mixpanel.track(
                                BuildADashTrackingEvents.EDIT_PAGE,
                                { page_id: actionPageId }
                              );
                            }}
                          >
                            Edit Page
                          </MenuItem>
                          {page && (
                            <MenuItem
                              fontSize="sm"
                              onClick={() => setEditingId(page.id)}
                            >
                              Rename
                            </MenuItem>
                          )}
                          <MenuItem
                            fontSize="sm"
                            onClick={() => {
                              // Analytics templates aren't in local pages, so
                              // clonePage's local-only lookup would no-op —
                              // route them through clonePageDraft instead.
                              if (page) {
                                clonePage(actionPageId);
                              } else {
                                onClose();
                                clonePageDraft(actionPageId);
                              }
                              mixpanel.track(
                                BuildADashTrackingEvents.CLONE_PAGE,
                                { page_id: actionPageId }
                              );
                            }}
                          >
                            Make a Copy
                          </MenuItem>
                          {page && (
                            <>
                              <MenuDivider />
                              <MenuItem
                                fontSize="sm"
                                onClick={() => {
                                  deletePage(page.id);
                                  mixpanel.track(
                                    BuildADashTrackingEvents.DELETE_PAGE,
                                    { page_id: page.id }
                                  );
                                }}
                              >
                                Delete
                              </MenuItem>
                            </>
                          )}
                        </MenuList>
                      </Menu>
                    ) : null;
                    return (
                      <DraggableTabLink
                        key={tab.id}
                        tab={tab}
                        variant="menu"
                        isActive={activeMenuTabId === tab.id}
                        onNavigate={onClose}
                        testId={`pages-menu-${tab.kind}-${tab.id}`}
                        editing={page ? editingId === page.id : false}
                        editableValue={tab.label}
                        onRename={(val) => {
                          if (page && val && val !== page.name) {
                            updatePage(page.id, { ...page, name: val });
                          }
                          setEditingId(null);
                        }}
                        trailing={trailing}
                      >
                        {highlight(tab.label)}
                      </DraggableTabLink>
                    );
                  });
                })()}
              </Flex>
            </SortableContext>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
